import React, { useEffect, useRef, useState } from 'react';
import { WifiOff, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import { getQueueCount } from '../utils/offlineQueue';

const timeAgo = (ts) => {
  if (!ts) return '';
  const mins = Math.max(0, Math.round((Date.now() - ts) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.round(mins / 60);
  return hrs === 1 ? '1 hour ago' : `${hrs} hours ago`;
};

// Slim connectivity banner for operators: amber while offline (with last-sync
// age when cached data is being shown + queued-action count), brief green
// flash on reconnect. Toasts when queued check-ins finish syncing.
export const OfflineBanner = () => {
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' ? !navigator.onLine : false);
  const [justBack, setJustBack] = useState(false);
  const [cachedAt, setCachedAt] = useState(null);
  const [pending, setPending] = useState(0);
  const backTimer = useRef(null);

  useEffect(() => {
    getQueueCount().then(setPending);
    const goOffline = () => { setOffline(true); setJustBack(false); };
    const goOnline = () => {
      setOffline(false);
      setCachedAt(null);
      setJustBack(true);
      clearTimeout(backTimer.current);
      backTimer.current = setTimeout(() => setJustBack(false), 3500);
    };
    const onCachedData = (e) => {
      setOffline(true);
      if (e.detail?.cachedAt) setCachedAt(e.detail.cachedAt);
    };
    const onQueued = (e) => setPending(e.detail?.pending ?? 0);
    const onSynced = (e) => {
      setPending(e.detail?.pending ?? 0);
      const n = e.detail?.synced || 0;
      if (n > 0) toast.success(`Synced ${n} offline check-in${n > 1 ? 's' : ''}`);
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    window.addEventListener('bf-offline-data', onCachedData);
    window.addEventListener('bf-offline-queued', onQueued);
    window.addEventListener('bf-offline-synced', onSynced);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
      window.removeEventListener('bf-offline-data', onCachedData);
      window.removeEventListener('bf-offline-queued', onQueued);
      window.removeEventListener('bf-offline-synced', onSynced);
      clearTimeout(backTimer.current);
    };
  }, []);

  if (!offline && !justBack) return null;

  return (
    <div
      data-testid="offline-banner"
      className={`fixed top-0 inset-x-0 z-[9999] flex items-center justify-center gap-2 px-4 py-1.5 text-xs font-medium shadow-sm transition-colors duration-300 ${
        offline ? 'bg-amber-500 text-white' : 'bg-emerald-500 text-white'
      }`}
      style={{ paddingTop: 'max(0.375rem, env(safe-area-inset-top))' }}
    >
      {offline ? (
        <>
          <WifiOff className="w-3.5 h-3.5 shrink-0" />
          <span data-testid="offline-banner-text">
            You're offline{cachedAt ? ` — showing data last synced ${timeAgo(cachedAt)}` : ' — showing your last synced data'}
            {pending > 0 ? ` · ${pending} check-in${pending > 1 ? 's' : ''} queued` : ''}
          </span>
        </>
      ) : (
        <>
          <Wifi className="w-3.5 h-3.5 shrink-0" />
          <span>Back online{pending > 0 ? ` — syncing ${pending} check-in${pending > 1 ? 's' : ''}…` : ''}</span>
        </>
      )}
    </div>
  );
};

export default OfflineBanner;
